import { useEffect, useMemo, useState } from "react";
import { Button, Icon, Input } from "@elaniintc/goes-design-system";
import type { NodeVariable } from "../types";
import { VariableSelector } from "./VariableSelector";
import type { Variable } from "./types";
import { getPlaceholder, getRestrictionFunction } from "./comparativeValues";
import ComparativeValueInputCalendar from "./ComparativeValueInput.calendar";

interface ComparativeValueInputArrayProps {
  variables: NodeVariable[];
  value?: string[];
  onChange?: (value: string[]) => void;
}

interface ArrayItem {
  id: string;
  value: string | Variable;
  type: "input" | "variable";
}

const parseValue = (val: string, index: number): ArrayItem => {
  try {
    const parsed = JSON.parse(val);
    if (parsed && typeof parsed === "object" && parsed.id) {
      return {
        id: `item-${index}-${parsed.id}`,
        value: parsed as Variable,
        type: "variable",
      };
    }
  } catch {
    // Si no es JSON válido, es un string
  }
  return {
    id: `item-${index}-${val || "empty"}`,
    value: val,
    type: "input",
  };
};

export const ComparativeValueInputArray = ({
  variables,
  value = [],
  onChange,
}: ComparativeValueInputArrayProps) => {
  const [items, setItems] = useState<ArrayItem[]>(() => {
    if (value.length === 0) {
      return [
        {
          id: `item-0-${Date.now()}`,
          value: "",
          type: "input" as const,
        },
      ];
    }
    return value.map((val, index) => parseValue(val, index));
  });

  // Sincronizar con cambios externos en value cuando cambia desde una fuente externa
  useEffect(() => {
    if (value.length > 0) {
      const newItems = value.map((val, index) => parseValue(val, index));
      // Solo actualizar si los valores realmente son diferentes
      const itemsChanged =
        newItems.length !== items.length ||
        newItems.some((newItem, idx) => {
          const oldItem = items[idx];
          if (!oldItem) return true;
          if (
            typeof newItem.value === "string" &&
            typeof oldItem.value === "string"
          ) {
            return newItem.value !== oldItem.value;
          }
          if (
            typeof newItem.value === "object" &&
            typeof oldItem.value === "object"
          ) {
            return (
              JSON.stringify(newItem.value) !== JSON.stringify(oldItem.value)
            );
          }
          return true;
        });

      if (itemsChanged) {
        setItems(newItems);
      }
    } else if (
      value.length === 0 &&
      items.length > 0 &&
      items[0].value !== ""
    ) {
      // Si value es un array vacío y los items tienen datos, resetear
      setItems([
        {
          id: `item-0-${Date.now()}`,
          value: "",
          type: "input" as const,
        },
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Detectar el tipo de la primera variable seleccionada
  const detectedType = useMemo(() => {
    const firstVariableItem = items.find(
      (item) => item.type === "variable" && typeof item.value === "object",
    );
    return firstVariableItem && typeof firstVariableItem.value === "object"
      ? firstVariableItem.value.type
      : undefined;
  }, [items]);

  const filteredVariables = useMemo(() => {
    return variables
      .map((v) => ({
        ...v,
        variables: (v.variables ?? []).filter((variable) => {
          const isNotArray = variable.type !== "array";
          return detectedType
            ? variable.type === detectedType && isNotArray
            : isNotArray;
        }),
      }))
      .filter((v) => v.variables.length > 0);
  }, [variables, detectedType]);
  const handleAddItem = () => {
    // No permitir agregar más de 10 elementos
    if (items.length >= 10) return;

    const newItem: ArrayItem = {
      id: `item-${items.length}-${Date.now()}`,
      value: "",
      type: "input",
    };
    const updatedItems = [...items, newItem];
    setItems(updatedItems);
    notifyChange(updatedItems);
  };

  const handleRemoveItem = (id: string) => {
    // No permitir eliminar si solo hay un elemento
    if (items.length <= 1) return;

    const updatedItems = items.filter((item) => item.id !== id);
    setItems(updatedItems);
    notifyChange(updatedItems);
  };

  const handleItemValueChange = (id: string, newValue: string | Variable) => {
    const updatedItems = items.map((item) =>
      item.id === id ? { ...item, value: newValue } : item,
    );
    setItems(updatedItems);
    notifyChange(updatedItems);
  };

  const handleToggleType = (id: string) => {
    const updatedItems = items.map((item) =>
      item.id === id
        ? {
            ...item,
            type: (item.type === "input" ? "variable" : "input") as
              | "input"
              | "variable",
            value: "", // Resetear valor al cambiar de tipo
          }
        : item,
    );
    setItems(updatedItems);
    notifyChange(updatedItems);
  };

  const notifyChange = (updatedItems: ArrayItem[]) => {
    if (onChange) {
      onChange(
        updatedItems.map((item) =>
          typeof item.value === "string"
            ? item.value
            : JSON.stringify(item.value),
        ),
      );
    }
  };

  return (
    <div className="flex flex-col gap-2 pt-2 w-full">
      {items.map((item) => (
        <div key={item.id} className="flex h-11 items-center">
          <Button
            group="left"
            variant="quiet"
            type="button"
            className="font-mono h-full"
            data-testid={`toggle-comparative-value-type-btn-${item.id}`}
            onClick={() => handleToggleType(item.id)}
          >
            {item.type === "input" ? "inp" : "var"}
          </Button>
          {item.type === "input" ? (
            detectedType === "date" ? (
              <ComparativeValueInputCalendar
                group="right"
                defaultDate={typeof item.value === "string" ? item.value : ""}
                onChangeDate={(date) => handleItemValueChange(item.id, date)}
              />
            ) : (
              <Input
                group="right"
                placeholder={getPlaceholder(detectedType || "string")}
                className="w-full h-full"
                value={typeof item.value === "string" ? item.value : ""}
                onChange={(e) => {
                  const func = getRestrictionFunction(detectedType || "");
                  handleItemValueChange(item.id, func(e.target.value));
                }}
              />
            )
          ) : (
            <VariableSelector
              variables={filteredVariables}
              selectedVariable={
                typeof item.value === "object" ? item.value : null
              }
              onSelectVariable={(variable) =>
                handleItemValueChange(item.id, variable || "")
              }
              triggerGroup="right"
            />
          )}
          <Button
            variant="ghost"
            size="icon"
            aria-label="Eliminar item"
            className="size-7 ml-1"
            type="button"
            onClick={() => handleRemoveItem(item.id)}
            disabled={items.length <= 1}
          >
            <Icon type="far" name="times" />
          </Button>
        </div>
      ))}
      <div className="mt-2">
        <Button
          variant="tertiary"
          size="xs"
          onClick={handleAddItem}
          type="button"
          disabled={items.length >= 10}
        >
          Añadir item
          <Icon type="far" name="plus" className="ml-2" />
        </Button>
      </div>
    </div>
  );
};
